import { observer } from 'mobx-react-lite';
import { EditingLabelCopy } from './AttachmentInfoView.locstring.json';
import { EditingLabel } from '../strings.locstring.json';
import { getSaveToCloudLinkIfAvailable } from '../utils/getSaveToCloudLinkIfAvailable';
import { Icon } from '@fluentui/react/lib/Icon';
import { logUsage } from 'owa-analytics';
import { AttachmentFullViewState } from 'owa-attachment-full-data';
import { AttachmentModel, TypeOfAttachment } from 'owa-attachment-model-store';
import { getFirstLineTitle } from 'owa-attachment-view/lib/utils/getAttachmentInfo';
import { ControlIcons } from 'owa-control-icons';
import loc, { isStringNullOrWhiteSpace } from 'owa-localize';
import { SavingLabel } from 'owa-locstrings/lib/strings/savinglabel.locstring.json';
import { highlightTermsInHtmlElement } from 'owa-mail-highlight-terms';
import { AttachmentPreviewMode } from 'owa-sxs-store';
import * as React from 'react';
import getAttachmentInfoSecondLineInfo, {
    AttachmentInfoSecondLineInfo,
} from '../utils/getAttachmentInfoSecondLineInfo';
import { getShouldHideSurfaceActionButton } from './SurfaceActions/getShouldHideSurfaceActionButton';
import { SurfaceAction } from '../types/SurfaceAction';

export interface AttachmentInfoProps {
    attachmentViewState: AttachmentFullViewState;
    attachment: AttachmentModel;
    isForImageView: boolean;
    isActionsContainerVisible: boolean;
    previewState: AttachmentPreviewMode;
}

const styles = require('./AttachmentInfoView.scss');
const classNames = require('classnames/bind').bind(styles);

export default observer(function AttachmentInfoView(props: AttachmentInfoProps) {
    const onClick = (event: React.MouseEvent<unknown>) => {
        logUsage(
            'WVNHyperLinkOnAttachmentClicked',
            {}, // customData
            {
                isCore: true,
                cosmosOnlyData: JSON.stringify({
                    attachmentId: props.attachmentViewState.attachmentId.Id,
                }),
            }
        );
        event.stopPropagation();
    };
    const { attachmentViewState, attachment, isForImageView } = props;
    const firstLineTitle = getFirstLineTitle(attachmentViewState, attachment);
    const conditionalStyles = {
        attachmentInfoForImageView: isForImageView,
    };
    const actionSucceededConditionalStyles = {
        colorGreen: !isForImageView,
    };
    const actionFailedConditionalStyles = {
        colorRed: !isForImageView,
    };
    const saveToCloudLink = getSaveToCloudLinkIfAvailable(
        attachment.model.Name,
        attachmentViewState.isCloudy,
        attachmentViewState.isReadOnly,
        attachment.model.AttachmentOriginalUrl
    );
    const secondLineInfo: AttachmentInfoSecondLineInfo = getAttachmentInfoSecondLineInfo(
        attachmentViewState,
        attachment,
        !isStringNullOrWhiteSpace(saveToCloudLink),
        attachment.model.Name
    );
    const firstLineConditionalStyles = {
        expiredAttachment:
            secondLineInfo.useExpiredAttachmentStyling && !attachmentViewState.isReadOnly,
    };
    const firstLineStyle = getFirstLineStyle(
        isForImageView,
        attachmentViewState.isReadOnly,
        props.isActionsContainerVisible
    );
    const hasIcon: boolean =
        secondLineInfo.shouldDisplayFailureIcon || secondLineInfo.shouldDisplaySuccessIcon;
    const secondLineStyle = getSecondLineStyle(
        isForImageView,
        hasIcon,
        props.isActionsContainerVisible,
        attachmentViewState
    );
    let attachmentLabelText: String;
    switch (props.previewState) {
        case AttachmentPreviewMode.Edit:
            attachmentLabelText =
                attachment.type === TypeOfAttachment.Reference
                    ? loc(EditingLabel)
                    : loc(EditingLabelCopy);
            break;
        case AttachmentPreviewMode.SelectedAndSaving:
        case AttachmentPreviewMode.NotSelectedAndSaving:
            attachmentLabelText = loc(SavingLabel);
            break;
        default:
            attachmentLabelText = '';
    }
    const isAttachmentInEditMode: boolean = attachmentLabelText !== '';
    const secondLineConditionalStyles = {
        expiredAttachment:
            secondLineInfo.useExpiredAttachmentStyling && !attachmentViewState.isReadOnly,
        attachmentLabelTextEditMode: isAttachmentInEditMode,
    };
    return (
        <div className={classNames(conditionalStyles)}>
            <div
                className={classNames(
                    firstLineConditionalStyles,
                    styles.firstLineCommonStyle,
                    firstLineStyle,
                    styles.textOverFlow
                )}
                title={firstLineTitle}
                ref={highlightAttachmentName}>
                {attachment.model.Name}
            </div>

            <div title={secondLineInfo.title ? secondLineInfo.title : firstLineTitle}>
                {secondLineInfo.shouldDisplaySuccessIcon && (
                    <Icon
                        iconName={ControlIcons.Accept}
                        className={classNames(
                            styles.actionComplete,
                            actionSucceededConditionalStyles
                        )}
                    />
                )}
                {secondLineInfo.shouldDisplayFailureIcon && (
                    <Icon
                        iconName={ControlIcons.Cancel}
                        className={classNames(styles.actionComplete, actionFailedConditionalStyles)}
                    />
                )}

                {isAttachmentInEditMode && (
                    <Icon
                        className={styles.attachmentLabelIconEditMode}
                        iconName={ControlIcons.Edit}
                    />
                )}

                {isStringNullOrWhiteSpace(saveToCloudLink) ? (
                    <span
                        className={classNames(
                            secondLineConditionalStyles,
                            styles.secondLineCommonStyle,
                            secondLineStyle,
                            styles.textOverFlow
                        )}>
                        {isAttachmentInEditMode ? attachmentLabelText : secondLineInfo.text}
                    </span>
                ) : (
                    <div onClick={onClick} className={styles.oneDriveLinkContainer}>
                        <a
                            href={saveToCloudLink}
                            target="_blank"
                            className={classNames(secondLineStyle, styles.textOverFlow)}>
                            {secondLineInfo.text}
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
});

function highlightAttachmentName(ref: HTMLDivElement) {
    highlightTermsInHtmlElement(ref);
}

function getFirstLineStyle(
    isImageView: boolean,
    isReadOnly: boolean,
    isActionsContainerShown: boolean
): string {
    if (isImageView) {
        // The first line always has the same width for images
        return styles.firstLineForImageView;
    } else if (!isReadOnly && isActionsContainerShown) {
        // If the actions container is shown, and the attachment is not read-only, then the first line has a shorter width than normal
        return styles.firstLineWithActions;
    }
    return styles.firstLine;
}

function getSecondLineStyle(
    isImageView: boolean,
    hasIcon: boolean,
    isActionsContainerShown: boolean,
    attachmentViewState: AttachmentFullViewState
): string {
    const isActionsContainerShownWith3Actions =
        isActionsContainerShown &&
        !getShouldHideSurfaceActionButton(SurfaceAction.openAndEdit, attachmentViewState);
    if (isImageView) {
        if (hasIcon) {
            if (isActionsContainerShownWith3Actions) {
                return styles.secondLineForImageViewWithIconAnd3Actions;
            } else if (isActionsContainerShown) {
                return styles.secondLineForImageViewWithIconAndAction;
            } else {
                return styles.secondLineForImageViewWithIcon;
            }
        } else {
            if (isActionsContainerShownWith3Actions) {
                return styles.secondLineForImageViewWith3Actions;
            } else if (isActionsContainerShown) {
                return styles.secondLineForImageViewWithAction;
            } else {
                return styles.secondLineForImageView;
            }
        }
    } else {
        if (hasIcon) {
            if (isActionsContainerShownWith3Actions) {
                return styles.secondLineWithIconAnd3Actions;
            } else if (isActionsContainerShown) {
                return styles.secondLineWithIconAndAction;
            } else {
                return styles.secondLineWithIcon;
            }
        } else {
            if (isActionsContainerShownWith3Actions) {
                return styles.secondLineWith3Actions;
            } else if (isActionsContainerShown) {
                return styles.secondLineWithAction;
            } else {
                return styles.secondLine;
            }
        }
    }
}
